/**
 * Adjust Panel Component (canvas-v2)
 *
 * Provides speed and volume controls for the selected clip.
 * - Speed slider: 0.1x - 4.0x (step 0.1) with preset buttons (0.5x, 1.0x, 1.5x, 2.0x)
 * - Volume slider: 0 - 1 (audio AND video clips)
 * - Mute toggle (audio AND video clips)
 * - Clip info display
 * - Empty state: "Select a clip to adjust."
 */

import { memo, useCallback } from 'react'
import type { TimelineClip, TimelineAudioClip } from './types'
import { createNadDebug } from '@ponpon/api-client'

const adjustLog = createNadDebug('dw:timeline-playback:render-contract')

interface AdjustPanelProps {
  /** Currently selected video clip (null if audio or none or multi-selection) */
  selectedVideoClip: TimelineClip | null

  /** Currently selected audio clip (null if video or none or multi-selection) */
  selectedAudioClip: TimelineAudioClip | null

  /**
   * Type of selected clip — 'mixed' when video+audio are selected together
   * (multi-selection across types). Single-clip selection is the only state
   * where the panel renders editing controls.
   */
  selectedClipType: 'video' | 'audio' | 'mixed' | null

  /** Number of currently-selected clips (used to render the multi-selection state) */
  multiSelectionCount?: number

  /** Callback when clip speed changes */
  onSpeedChange: (clipId: string, speed: number) => void

  /** Callback when audio clip volume changes */
  onAudioVolumeChange: (clipId: string, volume: number) => void

  /** Callback when audio clip mute is toggled */
  onAudioToggleMute: (clipId: string) => void

  /** Callback when video clip volume changes */
  onClipVolumeChange: (clipId: string, volume: number) => void

  /** Callback when video clip mute is toggled */
  onClipToggleMute: (clipId: string) => void

  /**
   * Callback when video clip fade-out duration changes (seconds).
   * Video-only: see RFC-0501. Audio fades have a separate plumbing path.
   */
  onClipFadeOutChange: (clipId: string, fadeOutS: number) => void

  /**
   * Callback when video clip fade-in duration changes (seconds).
   * Symmetric to fade-out. Both render via the live preview overlay.
   */
  onClipFadeInChange: (clipId: string, fadeInS: number) => void

  /**
   * RFC-0651 #12: extract the audio track of the selected video clip into
   * a new audio track placed directly below the source's video track. Async
   * — uploads a WAV through the existing project asset endpoint.
   */
  onExtractAudioFromClip?: (clip: TimelineClip) => Promise<void> | void
}

const SPEED_PRESETS = [0.5, 1.0, 1.5, 2.0]

export const AdjustPanel = memo(function AdjustPanel({
  selectedVideoClip,
  selectedAudioClip,
  selectedClipType,
  multiSelectionCount = 0,
  onSpeedChange,
  onAudioVolumeChange,
  onAudioToggleMute,
  onClipVolumeChange,
  onClipToggleMute,
  onClipFadeOutChange,
  onClipFadeInChange,
  onExtractAudioFromClip,
}: AdjustPanelProps) {
  const clip = selectedClipType === 'video' ? selectedVideoClip : selectedClipType === 'audio' ? selectedAudioClip : null

  // Audio-control surface — works for both audio AND video clips since both
  // now carry `volume` and `muted` fields.
  const audioControlClip = selectedClipType === 'audio' ? selectedAudioClip : selectedClipType === 'video' ? selectedVideoClip : null
  const audioControlClipType: 'video' | 'audio' | null =
    selectedClipType === 'video' || selectedClipType === 'audio' ? selectedClipType : null

  const handleSpeedSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!clip) return
      const speed = parseFloat(e.target.value)
      onSpeedChange(clip.id, speed)
      // Intentionally NOT blurring the slider here: the modal keydown handler
      // already lets Space through for any target. Blurring on every onChange
      // breaks keyboard adjustment (Arrow/Page/Home/End) by stealing focus
      // after the first nudge.
    },
    [clip, onSpeedChange]
  )

  const handleSpeedPreset = useCallback(
    (speed: number) => {
      if (!clip) return
      onSpeedChange(clip.id, speed)
      // Blur the just-clicked preset button so a subsequent Space goes to the
      // modal's playback toggle instead of re-clicking the focused button.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [clip, onSpeedChange]
  )

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!audioControlClip) return
      const volume = parseFloat(e.target.value)
      if (audioControlClipType === 'audio') {
        onAudioVolumeChange(audioControlClip.id, volume)
      } else {
        onClipVolumeChange(audioControlClip.id, volume)
      }
      // Same reasoning as handleSpeedSliderChange: no blur, so keyboard
      // adjustments (Arrow/Page/Home/End) keep working.
    },
    [audioControlClip, audioControlClipType, onAudioVolumeChange, onClipVolumeChange]
  )

  const handleToggleMute = useCallback(() => {
    if (!audioControlClip) return
    if (audioControlClipType === 'audio') {
      onAudioToggleMute(audioControlClip.id)
    } else {
      onClipToggleMute(audioControlClip.id)
    }
    // Blur the mute button so Space toggles playback instead of re-clicking it.
    // Buttons are special-cased here because Space on a focused button =
    // synthetic click (browser default), which would re-toggle mute.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    adjustLog({
      event: 'mute-toggle-blurred',
      clipId: audioControlClip.id,
      type: audioControlClipType,
    })
  }, [audioControlClip, audioControlClipType, onAudioToggleMute, onClipToggleMute])

  const handleFadeOutChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedVideoClip) return
      const fadeOutS = parseFloat(e.target.value)
      if (Number.isNaN(fadeOutS)) return
      onClipFadeOutChange(selectedVideoClip.id, fadeOutS)
      adjustLog({
        event: 'fade-out-change',
        feature: 'videoeditor-fade-out',
        clipId: selectedVideoClip.id,
        fadeOutS,
      })
    },
    [selectedVideoClip, onClipFadeOutChange]
  )

  const handleFadeInChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedVideoClip) return
      const fadeInS = parseFloat(e.target.value)
      if (Number.isNaN(fadeInS)) return
      onClipFadeInChange(selectedVideoClip.id, fadeInS)
      adjustLog({
        event: 'fade-in-change',
        feature: 'dw:video-editor-improvements',
        clipId: selectedVideoClip.id,
        fadeInS,
      })
    },
    [selectedVideoClip, onClipFadeInChange]
  )

  // Multi-selection / cross-type state: per-clip volume/speed editing isn't
  // defined for multi-clip; fall back to a hint instead of stale single-clip
  // controls. Use Ripple Delete or single-click to refocus.
  if (selectedClipType === 'mixed' || (multiSelectionCount > 1 && !clip)) {
    return (
      <div
        className="flex items-center justify-center h-full text-text-muted text-sm p-4 text-center"
        data-testid="timeline-adjust-multi-selection"
      >
        {multiSelectionCount} clips selected.
        <br />
        Select a single clip to edit speed and volume.
      </div>
    )
  }

  // Empty state
  if (!clip) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted text-sm p-4">
        Select a clip to adjust.
      </div>
    )
  }

  const currentSpeed = clip.speed
  const sliderTestId =
    audioControlClipType === 'video' ? 'timeline-clip-volume-slider' : 'timeline-audio-volume-slider'
  const muteTestId =
    audioControlClipType === 'video' ? 'timeline-clip-mute-button' : 'timeline-audio-mute-button'

  return (
    <div className="flex flex-col gap-4 p-3 h-full overflow-y-auto">
      {/* Clip info */}
      <div className="border-b border-border pb-3">
        <div className="text-foreground text-sm font-medium truncate">
          {selectedClipType === 'video'
            ? selectedVideoClip?.label
            : clip.sourcePath.split('/').pop() || 'Audio'
          }
        </div>
        <div className="text-text-muted text-xs mt-0.5">
          {selectedClipType === 'video' ? 'Video clip' : selectedClipType === 'audio' ? 'Audio clip' : 'Clip'}
          {' | '}
          {formatDuration(clip.effectiveDuration)}
        </div>
      </div>

      {/* Speed control */}
      <div>
        <label className="text-text-secondary text-xs font-medium block mb-2">
          Speed
        </label>

        {/* Speed slider */}
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0.1"
            max="4.0"
            step="0.1"
            value={currentSpeed}
            onChange={handleSpeedSliderChange}
            className="flex-1"
            data-testid="timeline-speed-slider"
          />
          <span className="text-foreground text-sm font-mono min-w-[4ch] text-right">
            {currentSpeed.toFixed(1)}x
          </span>
        </div>

        {/* Speed presets */}
        <div className="flex gap-1.5 mt-2">
          {SPEED_PRESETS.map((preset) => (
            <button
              key={preset}
              onClick={() => handleSpeedPreset(preset)}
              className={`
                flex-1 px-2 py-1 text-xs rounded border transition-colors
                ${Math.abs(currentSpeed - preset) < 0.05
                  ? 'bg-blue-500/20 border-blue-400 text-blue-300'
                  : 'bg-card border-border text-text-secondary hover:bg-card-hover'
                }
              `}
            >
              {preset}x
            </button>
          ))}
        </div>
      </div>

      {/* Volume control (audio AND video clips) */}
      {audioControlClip && (
        <div>
          <label className="text-text-secondary text-xs font-medium block mb-2">
            Volume
          </label>

          <div className="flex items-center gap-2">
            {/* Mute toggle */}
            <button
              onClick={handleToggleMute}
              className={`
                p-1.5 rounded border transition-colors text-sm
                ${audioControlClip.muted
                  ? 'bg-red-500/20 border-red-400 text-red-300'
                  : 'bg-card border-border text-text-secondary hover:bg-card-hover'
                }
              `}
              title={audioControlClip.muted ? 'Unmute' : 'Mute'}
              data-testid={muteTestId}
            >
              {audioControlClip.muted ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>

            {/* Volume slider */}
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={audioControlClip.volume}
              onChange={handleVolumeChange}
              className="flex-1"
              disabled={audioControlClip.muted}
              data-testid={sliderTestId}
            />
            <span className="text-foreground text-sm font-mono min-w-[4ch] text-right">
              {Math.round(audioControlClip.volume * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Fade-in control (video clips only) */}
      {selectedClipType === 'video' && selectedVideoClip && (
        (() => {
          const maxFade = Math.max(0, selectedVideoClip.effectiveDuration)
          const currentFade = Math.max(0, Math.min(maxFade, selectedVideoClip.fadeInS ?? 0))
          return (
            <div data-testid="timeline-clip-fade-in-section">
              <label className="text-text-secondary text-xs font-medium block mb-2">
                Fade in
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={maxFade}
                  step={0.1}
                  value={currentFade}
                  onChange={handleFadeInChange}
                  className="flex-1"
                  data-testid="timeline-clip-fade-in-slider"
                  aria-label="Video clip fade in duration in seconds"
                />
                <span
                  className="text-foreground text-sm font-mono min-w-[4ch] text-right"
                  data-testid="timeline-clip-fade-in-value"
                >
                  {currentFade.toFixed(1)}s
                </span>
              </div>
              <div className="text-text-muted text-xxs mt-1">
                Fades the clip in from black over the first {currentFade.toFixed(1)}s. Set to 0 to disable.
              </div>
            </div>
          )
        })()
      )}

      {/* Fade-out control (video clips only) */}
      {selectedClipType === 'video' && selectedVideoClip && (
        (() => {
          const maxFade = Math.max(0, selectedVideoClip.effectiveDuration)
          const currentFade = Math.max(0, Math.min(maxFade, selectedVideoClip.fadeOutS ?? 0))
          return (
            <div data-testid="timeline-clip-fade-out-section">
              <label className="text-text-secondary text-xs font-medium block mb-2">
                Fade out
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={maxFade}
                  step={0.1}
                  value={currentFade}
                  onChange={handleFadeOutChange}
                  className="flex-1"
                  data-testid="timeline-clip-fade-out-slider"
                  aria-label="Video clip fade out duration in seconds"
                />
                <span
                  className="text-foreground text-sm font-mono min-w-[4ch] text-right"
                  data-testid="timeline-clip-fade-out-value"
                >
                  {currentFade.toFixed(1)}s
                </span>
              </div>
              <div className="text-text-muted text-xxs mt-1">
                Fades the clip to black over the last {currentFade.toFixed(1)}s. Set to 0 to disable.
              </div>
            </div>
          )
        })()
      )}

      {/* RFC-0651 #12: Extract Audio (video clips only) */}
      {selectedClipType === 'video' && selectedVideoClip && (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => {
              if (selectedVideoClip && onExtractAudioFromClip) {
                void onExtractAudioFromClip(selectedVideoClip)
              }
            }}
            disabled={!onExtractAudioFromClip}
            data-testid="adjust-panel-extract-audio"
            className="self-start rounded border border-border bg-surface-1 px-3 py-1 text-xs text-text-secondary transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Extract Audio
          </button>
          <div className="text-xxs text-text-muted">
            Creates a new audio track directly below this video&apos;s track with the
            extracted audio.
          </div>
        </div>
      )}

      {/* Additional clip details */}
      <div className="mt-auto border-t border-border pt-3">
        <div className="text-text-muted text-xxs space-y-1">
          <div className="flex justify-between">
            <span>Original duration</span>
            <span>{formatDuration(clip.originalDuration)}</span>
          </div>
          <div className="flex justify-between">
            <span>Trim start</span>
            <span>{clip.trimStart.toFixed(2)}s</span>
          </div>
          <div className="flex justify-between">
            <span>Trim end</span>
            <span>{clip.trimEnd.toFixed(2)}s</span>
          </div>
          {selectedClipType === 'audio' && selectedAudioClip && (
            <div className="flex justify-between">
              <span>Start offset</span>
              <span>{selectedAudioClip.startOffset.toFixed(2)}s</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
